import { useCallback, useEffect, useRef, useState } from 'react';

import { guardChunks } from './orders.js';

/** reads.js MAX_PAGE: the most one GET /orders page may hold. */
const PAGE = 200;
/** A queue the checker acts on in bulk is read whole, up to this many pages. */
const MAX_PAGES = 5;

/**
 * One GET /orders list (IP-89): the first page - or, with `all`, every page
 * up to MAX_PAGES - then reload() and loadMore() after the cursor. A slower
 * answer to an older request never overwrites a newer one.
 *
 * @param {object} api the Console API (api/endpoints.js)
 * @param {object} query orders.list's query: states, orderTypes, queue, fundId, dealingDate
 * @param {{ all?: boolean }} [options]
 */
export function useOrderList(api, query, { all = false } = {}) {
    const key = JSON.stringify(query);
    const [state, setState] = useState({ orders: [], nextCursor: null, loading: true, error: null, refreshedAt: null });
    const latest = useRef(0);

    const read = useCallback(async (cursor, pages) => {
        let orders = [];
        let next = cursor;
        let count = 0;
        do {
            const answer = await api.orders.list({ ...JSON.parse(key), limit: PAGE, cursor: next ?? undefined });
            orders = orders.concat(answer.orders ?? []);
            next = answer.nextCursor ?? null;
            count += 1;
        } while (next && count < pages);
        return { orders, nextCursor: next };
    }, [api, key]);

    const reload = useCallback(async () => {
        const mine = ++latest.current;
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const { orders, nextCursor } = await read(null, all ? MAX_PAGES : 1);
            if (mine === latest.current) setState({ orders, nextCursor, loading: false, error: null, refreshedAt: new Date() });
        } catch (e) {
            if (mine === latest.current) setState((s) => ({ ...s, loading: false, error: e.message }));
        }
    }, [read, all]);

    const loadMore = useCallback(async () => {
        if (!state.nextCursor) return;
        const mine = ++latest.current;
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const { orders, nextCursor } = await read(state.nextCursor, 1);
            if (mine === latest.current) setState((s) => ({ ...s, orders: s.orders.concat(orders), nextCursor, loading: false }));
        } catch (e) {
            if (mine === latest.current) setState((s) => ({ ...s, loading: false, error: e.message }));
        }
    }, [read, state.nextCursor]);

    useEffect(() => {
        reload();
    }, [reload]);

    return { ...state, reload, loadMore };
}

/**
 * The server's pricing guard (GET /pricing/guard, IP-62) over these dealing
 * dates - the same answer ORDER_APPROVE will read before it posts anything.
 * `token` asks again when it changes (after an action).
 *
 * @returns {{ entries: Array<{ date: string, missingFunds: string[] }>, loading: boolean, error: string|null }}
 */
export function usePricingGuard(api, dates, token) {
    const key = dates.join(',');
    const [state, setState] = useState({ entries: [], loading: false, error: null });

    useEffect(() => {
        let live = true;
        if (!key) {
            setState({ entries: [], loading: false, error: null });
            return undefined;
        }
        setState((s) => ({ ...s, loading: true, error: null }));
        Promise.all(guardChunks(key.split(',')).map((chunk) => api.pricing.guard(chunk)))
            .then((answers) => {
                if (live) setState({ entries: answers.flatMap((a) => a.dates ?? []), loading: false, error: null });
            })
            .catch((e) => {
                if (live) setState({ entries: [], loading: false, error: e.message });
            });
        return () => {
            live = false;
        };
    }, [api, key, token]);

    return state;
}
